import { useEffect, useRef, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../auth/authHooks";
import { useSignOut } from "../auth/useSignOut";
import AccessPending from "../../../pages/AccessPending";
import { consumeSafeReturn, resolvePostAuthDestination } from "../../../lib/safeReturn";
import { marketingSiteUrl } from "../../../config/siteOrigin";
import { useCanonicalProductPath } from "../navigation/ProductNavigation";

function SignOutAction({ className }: { className: string }) {
  const signOut = useSignOut();
  return <button type="button" onClick={signOut} className={className}>Sign out</button>;
}

export function ProductRecoveryScreen({
  kind,
}: {
  kind: "not-found" | "callback-failure" | "session-check-failure" | "api-unavailable" | "offline" | "redirect-loop" | "product-access-empty" | "product-access-invalid";
}) {
  const heading = useRef<HTMLHeadingElement>(null);
  const location = useLocation();
  const canonicalProductPath = useCanonicalProductPath();
  useEffect(() => heading.current?.focus(), [kind]);

  const content = {
    "not-found": ["Page not found", "That page is not part of the Pinpoint app."],
    "callback-failure": ["Sign-in could not be completed", "The sign-in provider did not complete the request."],
    "session-check-failure": ["We couldn't verify your session", "Pinpoint could not finish checking your existing sign-in. Retry the check or return to the public site."],
    "api-unavailable": ["Pinpoint is temporarily unavailable", "We could not load current product data. No demonstration data has been substituted."],
    "offline": ["You appear to be offline", "Reconnect to the internet, then retry. No demonstration data has been substituted."],
    "redirect-loop": ["We stopped a redirect loop", "Automatic navigation stopped so you can choose a safe destination."],
    "product-access-empty": ["No learning area is available", "Your account is signed in, but it does not currently include SAT or Family access."],
    "product-access-invalid": ["We couldn't determine your learning area", "Your account access settings could not be verified. Retry or contact support."],
  } as const;
  const [title, body] = content[kind];
  const previousPath = canonicalProductPath(resolvePostAuthDestination(
    (location.state as { from?: string } | null)?.from,
  ));
  const practicePath = canonicalProductPath("/practice");
  const loginPath = canonicalProductPath("/login");

  const primary = "rounded bg-blue-600 px-4 py-2 text-white";
  const secondary = "rounded border px-4 py-2";
  const headingId = `product-${kind}-heading`;

  return (
    <main aria-labelledby={headingId} className="flex min-h-screen items-center justify-center p-6 text-center" data-testid={`product-${kind}`}>
      <div className="max-w-lg">
        <h1 id={headingId} ref={heading} tabIndex={-1} className="text-3xl font-bold">{title}</h1>
        <p className="mt-4 text-slate-600">{body}</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          {kind === "not-found" && <a href={practicePath} className={primary}>Practice</a>}
          {kind === "not-found" && previousPath !== practicePath && <a href={previousPath} className={secondary}>Previous page</a>}
          {kind === "callback-failure" && <a href={loginPath} className={primary}>Retry sign-in</a>}
          {kind === "callback-failure" && <a href={marketingSiteUrl("/")} className={secondary}>Pinpoint home</a>}
          {(kind === "session-check-failure" || kind === "api-unavailable" || kind === "offline" || kind === "product-access-invalid") && (
            <button type="button" onClick={() => window.location.reload()} className={primary}>Retry</button>
          )}
          {(kind === "session-check-failure" || kind === "api-unavailable" || kind === "product-access-empty" || kind === "product-access-invalid") && <SignOutAction className={secondary} />}
          {kind === "session-check-failure" && <a href={marketingSiteUrl("/")} className={secondary}>Return to Marketing</a>}
          {kind === "redirect-loop" && <a href={practicePath} className={primary}>Practice</a>}
          {kind === "redirect-loop" && <a href={marketingSiteUrl("/")} className={secondary}>Pinpoint home</a>}
          {kind === "redirect-loop" && <a href={loginPath} className={secondary}>Log in</a>}
          {(kind === "redirect-loop" || kind === "api-unavailable" || kind === "offline") && (
            <a href="mailto:yangmu@pinpointedu.com" className={secondary}>{kind === "api-unavailable" ? "Status and help" : "Support"}</a>
          )}
          {(kind === "product-access-empty" || kind === "product-access-invalid") && (
            <a href="mailto:yangmu@pinpointedu.com" className={secondary}>Support</a>
          )}
        </div>
      </div>
    </main>
  );
}

export function AccessPendingRoute(): ReactNode {
  const { user, verifying, accessPending } = useAuth();
  const location = useLocation();
  const canonicalProductPath = useCanonicalProductPath();
  const hasToken = Boolean(localStorage.getItem("access_token"));

  if (verifying) return <div role="status" aria-live="polite">Checking your access...</div>;
  if (user?.email) {
    return <Navigate to={canonicalProductPath(consumeSafeReturn())} replace />;
  }
  if (!hasToken) {
    return <Navigate to={canonicalProductPath('/login')} state={{ returnTo: `${location.pathname}${location.search}${location.hash}` }} replace />;
  }
  return accessPending ? <AccessPending /> : <ProductRecoveryScreen kind="api-unavailable" />;
}

export function ProductNotFound() {
  const canonicalProductPath = useCanonicalProductPath();
  return (
    <div data-testid="product-shell-anchor" className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white px-5 py-4">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-4">
          <a href={canonicalProductPath("/today")} className="text-lg font-bold text-slate-900">Pinpoint</a>
          <nav aria-label="Product" className="flex items-center gap-4 text-sm font-medium text-slate-700">
            <a href={canonicalProductPath("/today")}>Today</a>
            <a href={canonicalProductPath("/practice")}>Practice</a>
            <a href={canonicalProductPath("/insights")}>Insights</a>
          </nav>
        </div>
      </header>
      <ProductRecoveryScreen kind="not-found" />
    </div>
  );
}
